import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { simDateShort } from '@/utils/format';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { activeDeclineFlagForCustomer, flagNoDepositDecline } from '@/domain/noDepositDeclineFlow';
import { actorLabel, currentActorId } from '@/domain/actor';
import { declinedHoldDepositLink } from '@/domain/depositFlow';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { resolveNoDepositOverrideIntent } from '@/api/overrideIntents';
import { intentFailureCopy, type IntentFailure } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { useUIStore } from '@/state/uiStore';
import type { Booking, Deposit, NoDepositDeclineFlag, OverrideRequest } from '@/domain/types';

/**
 * No-deposit approval flow.
 *
 * A booking's deposit at or below the studio minimum can't be taken directly by
 * a non-privileged persona. Instead they raise an OverrideRequest with a
 * mandatory reason; the booking is created `held` and the request lands in
 * Georgina's Approvals queue. Georgina/Owner approve (booking → `confirmed`,
 * depositWaived) or reject (booking stays `held` for follow-up). Privileged
 * personas who waive directly record a pre-approved request for the same audit
 * trail. Everything is logged via appendActivity and published on the eventBus,
 * mirroring depositFlow / sessionFlow.
 */

/**
 * The studio's minimum acceptable deposit without approval, in AUD. A deposit at
 * or below this floor requires a no-deposit override. Defaults to 0, so only a
 * genuinely zero deposit trips the control today; raise it to route nominal
 * underpayments through approval too (single source of truth for the gate).
 */
export const STUDIO_MINIMUM_DEPOSIT_AUD = 0;

/** True when a deposit of this amount needs a no-deposit override to proceed. */
export function requiresNoDepositApproval(amountAud: number): boolean {
  return amountAud <= STUDIO_MINIMUM_DEPOSIT_AUD;
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

/** The live pending override request for a booking, if any (else undefined). */
export function pendingOverrideRequestForBooking(
  bookingId: string,
  requests: Record<string, OverrideRequest>,
): OverrideRequest | undefined {
  return Object.values(requests).find(
    (r) => r.bookingId === bookingId && r.status === 'pending',
  );
}

/**
 * The approval behind a no-deposit booking (B3): the approved request that waived
 * its deposit, with who decided and when. Drives the "Approved by Ops · <name> ·
 * <date>" stamp on the booking card, customer record and Confirmations.
 */
export function approvedOverrideForBooking(
  bookingId: string,
  requests: Record<string, OverrideRequest>,
): OverrideRequest | undefined {
  return Object.values(requests)
    .filter((r) => r.bookingId === bookingId && r.status === 'approved' && r.resolvedBy)
    .sort((a, b) => (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? ''))[0];
}

/** The stamp text: "Approved by Ops · Georgina Gray · 16 Sep". */
export function approvedByOpsLabel(request: OverrideRequest): string {
  const when = request.resolvedAt ? ` · ${simDateShort(request.resolvedAt)}` : '';
  return `Approved by Ops · ${actorLabel(request.resolvedBy)}${when}`;
}

/** All pending override requests, oldest first (the order Georgina works them). */
export function listPendingOverrideRequests(
  requests: Record<string, OverrideRequest>,
): OverrideRequest[] {
  return Object.values(requests)
    .filter((r) => r.status === 'pending')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Roughly how long ago a request was raised, as a short human label
 * ("just now", "3m", "2h", "1d"). Prototype-only, minute-resolution.
 */
export function requestAgeLabel(createdAtIso: string, nowMs: number): string {
  const ms = Math.max(0, nowMs - new Date(createdAtIso).getTime());
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Raise a no-deposit override request against a held booking. The booking must
 * already exist (the booking form creates it `held`); the reason note is
 * mandatory — an empty/blank note is refused (no request created, no mutation),
 * so no UI path can queue an unexplained waiver.
 */
export function requestNoDepositOverride(input: {
  bookingId: string;
  /** The request id, when a server intent already wrote it (7b6n). */
  id?: string;
  /** Who raised it; defaults to the signed-in person (14ym9bv75ky). */
  requestedBy?: string;
  note: string;
}): OverrideRequest | undefined {
  const note = input.note.trim();
  const requestedBy = input.requestedBy ?? currentActorId() ?? 'system';
  if (!note) return undefined;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return undefined;
  // tv-9rt: a prior Ops decline closes the no-money route for this customer. The
  // agent cannot raise a second no-deposit override (no return trip through Ops,
  // diagrams 05/09) and must collect an actual deposit; the flag clears on payment.
  if (activeDeclineFlagForCustomer(booking.customerId)) return undefined;

  const at = nowIso();
  const request: OverrideRequest = {
    id: input.id ?? `ovr-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    bookingId: input.bookingId,
    requestedBy,
    note,
    status: 'pending',
    createdAt: at,
  };
  brain.upsertOverrideRequest(request);
  brain.appendActivity({
    id: `act-${request.id}`,
    type: 'override-requested',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `No-deposit approval requested by ${actorLabel(requestedBy)} — "${note}".`,
    timestamp: at,
  });
  const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `⏳ No-deposit approval requested · ${booking.studioId.replace('-', ' ')} · awaiting Georgina.`,
      { customerRefId: booking.customerId, timestamp: at },
    );
  }
  playSound('confirmation-tap');
  eventBus.publish('override:requested', {
    requestId: request.id,
    bookingId: input.bookingId,
  });
  return request;
}

/**
 * Transition a held booking to confirmed with the deposit waived. Used by the
 * privileged direct-waive path (a client write the server allows for Ops); the
 * approve path goes through the server intent below.
 */
function confirmWaivedBooking(booking: Booking): void {
  const brain = useBrainStore.getState();
  brain.upsertBooking({ ...booking, status: 'confirmed', depositWaived: true, overbookable: false });
  // Booking just reached `confirmed` — auto-send the pre-session waiver, same as
  // every other path into `confirmed`.
  onBookingConfirmed(booking.id);
}

/**
 * Approve or reject a pending override request (B3 / 14ym9bv75mp, diagrams 05 + 09).
 * Approve → the held booking is confirmed with the deposit waived, and the request
 * carries who approved it (the "approved by Ops" stamp). Reject → the customer's
 * decline flag is set (no no-money booking, no second request) and the $0 hold
 * gets a pending deposit link on the normal pay-by: paid, it confirms; unpaid, the
 * link sweep cancels it. Never on a request you raised yourself. Idempotent:
 * resolving an already-resolved request is a no-op.
 *
 * Served build: ONE server transaction (POST /api/intents/resolve-no-deposit-override)
 * decides; the store mirrors it without bridge PUTs and restores it on a refusal.
 */
export function resolveNoDepositOverride(input: {
  requestId: string;
  approve: boolean;
  /** Who decided; defaults to the signed-in person (14ym9bv75ky). */
  resolvedBy?: string;
  resolutionNote?: string;
}): OverrideRequest | undefined {
  const resolvedBy = input.resolvedBy ?? currentActorId() ?? 'system';
  const brain = useBrainStore.getState();
  const request = brain.overrideRequests[input.requestId];
  if (!request || request.status !== 'pending') return undefined;
  if (request.requestedBy === resolvedBy) return undefined; // maker-checker: never your own request
  const note = input.resolutionNote?.trim() || undefined;
  if (!input.approve && !note) return undefined; // a decline needs a reason

  const at = nowIso();
  const resolved: OverrideRequest = {
    ...request,
    status: input.approve ? 'approved' : 'rejected',
    resolvedBy,
    resolvedAt: at,
    resolutionNote: note,
  };
  const booking = brain.bookings[request.bookingId];
  const link: Deposit | undefined =
    !input.approve && booking && booking.status === 'held' && !booking.depositId
      ? declinedHoldDepositLink(booking)
      : undefined;
  const prev = {
    booking,
    flag: booking ? brain.noDepositDeclineFlags[`ndf-${booking.customerId}`] : undefined,
  };

  const apply = () => {
    const b = useBrainStore.getState();
    b.upsertOverrideRequest(resolved);
    if (!booking) return;
    if (input.approve) {
      if (booking.status === 'held') b.upsertBooking({ ...booking, status: 'confirmed', depositWaived: true, overbookable: false });
      return;
    }
    flagNoDepositDecline({ customerId: booking.customerId, sourceBookingId: booking.id, reason: note, by: resolvedBy });
    if (link) {
      b.upsertDeposit(link);
      b.upsertBooking({ ...booking, depositId: link.id });
    }
  };

  if (isStoreFallbackEnabled()) {
    apply();
    if (booking && input.approve && booking.status === 'held') onBookingConfirmed(booking.id);
  } else {
    const pairs: Array<readonly [string, string]> = [
      ['overrideRequests', request.id],
      ...(booking ? ([['bookings', booking.id], ['noDepositDeclineFlags', `ndf-${booking.customerId}`]] as const) : []),
      ...(link ? ([['deposits', link.id]] as const) : []),
    ];
    withServerAuthoredWrites(pairs, apply);
    const revert = (failure: IntentFailure) => {
      withServerAuthoredWrites(pairs, () => {
        useBrainStore.setState((s) => {
          const deposits = { ...s.deposits };
          if (link) delete deposits[link.id];
          const flags: Record<string, NoDepositDeclineFlag> = { ...s.noDepositDeclineFlags };
          if (booking) {
            if (prev.flag) flags[prev.flag.id] = prev.flag;
            else delete flags[`ndf-${booking.customerId}`];
          }
          return {
            overrideRequests: { ...s.overrideRequests, [request.id]: request },
            bookings: booking ? { ...s.bookings, [booking.id]: booking } : s.bookings,
            noDepositDeclineFlags: flags,
            deposits,
          };
        });
      });
      const retry = intentFailureCopy(failure).retry;
      useUIStore.getState().pushNotification({
        intensity: 'critical',
        title: input.approve ? "Couldn't approve the no-deposit booking" : "Couldn't decline the no-deposit request",
        body: retry
          ? "We couldn't reach the server, so nothing changed. Try again."
          : `The server refused it: ${failure.message ?? failure.code ?? failure.error}. Nothing changed.`,
        ttlMs: 10000,
      });
    };
    void resolveNoDepositOverrideIntent({
      requestId: request.id,
      approve: input.approve,
      ...(note ? { note } : {}),
      ...(link ? { deposit: link } : {}),
      nowIso: at,
    })
      .then((res) => {
        if (!res.ok) return revert(res);
        // Another Ops seat decided it first: this tab's decision never happened.
        if (res.alreadyResolved) {
          return revert({
            ok: false,
            kind: 'rejected',
            status: 409,
            error: 'already-resolved',
            message: 'someone else in Ops already decided this request',
          });
        }
        if (booking && input.approve && booking.status === 'held') onBookingConfirmed(booking.id);
      })
      .catch(() => revert({ ok: false, kind: 'transient', status: 0, error: 'network' }));
  }

  if (booking) {
    brain.appendActivity({
      id: `act-${request.id}-resolved`,
      type: 'override-resolved',
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId: booking.artistId,
      body: input.approve
        ? `No-deposit booking approved by ${actorLabel(resolvedBy)} — confirmed with deposit waived.`
        : `No-deposit request declined by ${actorLabel(resolvedBy)}${note ? ` — "${note}"` : ''}. Held for a deposit until the pay-by.`,
      timestamp: at,
    });
    const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
    if (target) {
      brain.addAutoPing(
        target,
        input.approve
          ? `✅ No-deposit booking approved · ${booking.studioId.replace('-', ' ')} · confirmed, paying on the day.`
          : `⛔ No-deposit request declined · ${booking.studioId.replace('-', ' ')} · send the deposit link before the pay-by.`,
        { customerRefId: booking.customerId, timestamp: at },
      );
    }
  }

  playSound(input.approve ? 'booking-confirmed' : 'confirmation-tap');
  eventBus.publish('override:resolved', {
    requestId: request.id,
    bookingId: request.bookingId,
    approved: input.approve,
  });
  return resolved;
}

/**
 * Privileged direct path: Georgina/Owner waive a deposit at booking time. The
 * booking (already created `held`) is confirmed with depositWaived, and a
 * pre-approved OverrideRequest is recorded so the no-deposit booking still shows
 * an audit trail in the Approvals history. Note is mandatory, same as a request.
 */
export function waiveDepositDirectly(input: {
  bookingId: string;
  /** The request id, when a server intent already wrote it (7b6n). */
  id?: string;
  /** Who waived; defaults to the signed-in person (14ym9bv75ky). */
  by?: string;
  note: string;
}): OverrideRequest | undefined {
  const note = input.note.trim();
  const by = input.by ?? currentActorId() ?? 'system';
  if (!note) return undefined;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return undefined;

  const at = nowIso();
  const request: OverrideRequest = {
    id: input.id ?? `ovr-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    bookingId: input.bookingId,
    requestedBy: by,
    note,
    status: 'approved',
    createdAt: at,
    resolvedBy: by,
    resolvedAt: at,
  };
  brain.upsertOverrideRequest(request);
  confirmWaivedBooking(booking);
  brain.appendActivity({
    id: `act-${request.id}`,
    type: 'override-resolved',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${actorLabel(by)} waived the deposit directly — "${note}". Confirmed, paying on the day.`,
    timestamp: at,
  });
  const target = autoPingChannelFor('booking-created', booking.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `✅ No-deposit booking · ${booking.studioId.replace('-', ' ')} · waived by ${actorLabel(by)}, paying on the day.`,
      { customerRefId: booking.customerId, timestamp: at },
    );
  }
  playSound('booking-confirmed');
  eventBus.publish('override:resolved', {
    requestId: request.id,
    bookingId: input.bookingId,
    approved: true,
  });
  return request;
}
